import { useMemo } from "react";
import { MultiSelect } from "@/components/Base/MultiSelect";
import { Switch } from "@/components/Base/Switch";
import { useStore } from "@/stores/useStore";
import { getEmployeeName } from "@/lib/employees";
import {
  TaskListFilters,
  TaskStatus,
  UNASSIGNED_FILTER_VALUE,
} from "@/lib/taskSelectors";

export interface TaskFilterBarProps {
  filters: TaskListFilters;
  onChange: (filters: TaskListFilters) => void;
}

const STATUS_OPTIONS = [
  { label: "Open", value: TaskStatus.OPEN },
  { label: "Overdue", value: TaskStatus.OVERDUE },
  { label: "Completed", value: TaskStatus.COMPLETED },
];

const PILL_TRIGGER_CLASSES =
  "h-8 w-auto rounded-full border-0 bg-transparent py-0 pl-2.5 pr-2 text-xs font-medium text-muted-foreground hover:bg-card";

export const TaskFilterBar = ({ filters, onChange }: TaskFilterBarProps) => {
  const employees = useStore((s) => s.employees);

  // Same unfiltered employee list the task modal's assignee dropdown uses,
  // so the two surfaces always offer the same names.
  const assigneeOptions = useMemo(
    () => [
      { label: "Unassigned", value: UNASSIGNED_FILTER_VALUE },
      ...(employees ?? []).map((employee) => ({
        label: getEmployeeName(employee.id, employees ?? []),
        value: employee.id,
      })),
    ],
    [employees]
  );

  return (
    <div className="flex shrink-0 flex-wrap items-center gap-2 px-4 pb-3">
      <MultiSelect
        variant="ghost"
        triggerClassName={PILL_TRIGGER_CLASSES}
        options={STATUS_OPTIONS}
        value={filters.statuses}
        onChange={(statuses) =>
          onChange({ ...filters, statuses: statuses as TaskStatus[] })
        }
        placeholder="All statuses"
        itemsName="statuses"
      />
      <MultiSelect
        variant="ghost"
        triggerClassName={PILL_TRIGGER_CLASSES}
        options={assigneeOptions}
        value={filters.assigneeIds}
        onChange={(assigneeIds) => onChange({ ...filters, assigneeIds })}
        placeholder="Everyone"
        itemsName="assignees"
      />
      <label
        htmlFor="tasks-show-completed"
        className="flex h-8 cursor-pointer items-center gap-2 rounded-full border border-border bg-popover pl-2.5 pr-3 hover:border-ring"
      >
        <Switch
          id="tasks-show-completed"
          checked={filters.showCompleted}
          onCheckedChange={(showCompleted) =>
            onChange({ ...filters, showCompleted })
          }
        />
        <span className="text-xs font-medium text-muted-foreground">
          Show all completed
        </span>
      </label>
    </div>
  );
};
